// @ts-nocheck — frozen design snapshot, intentionally not type-checked
/**
 * AMZEL Nest — Site Detail v2-2 (Design Preview, erweitert)
 *
 * Erweitert v2 um pro-Site-Sektionen die Daniel angefordert hat:
 *  • Funkgeräte-Bestand (R7 / DP4800e / DM4600) mit "davon in Betrieb"
 *  • Akkus + Akku-Gesundheit + Ladestationen
 *  • Codeplugs pro Modell + Department (IT / OPS / Site-Standard)
 *  • Verschlüsselung & Zugangsdaten (AES-Keys + Repeater-Credentials)
 *    mit Security-Banner und Reveal-Prozess-Hinweis
 *  • RMA / Reparaturen: laufende Reparaturen + "RMA anmelden"-Button
 *
 * Public route: /preview/sites/:code/v2-2  (Daniel-requested)
 *
 * Self-contained (ERP-Preview-Pattern): alle Mock-Daten direkt in der Datei,
 * keine Abhängigkeit zu ../data/pad2.ts außer für Site-Adresse/PoC/Repeater.
 * v2 (Snapshot-URL /v2) bleibt 1:1 unangetastet als Vergleichs-Referenz.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertTriangle,
  Antenna,
  Battery,
  BatteryCharging,
  CalendarClock,
  CheckCircle2,
  CheckCircle,
  ChevronDown,
  CircleDot,
  Download,
  Eye,
  FileArchive,
  FileText,
  History,
  KeyRound,
  Lock,
  Mail,
  MapPin,
  PackageOpen,
  Radio,
  RadioTower,
  ShieldCheck,
  Smartphone,
  Users,
  Wrench,
} from "lucide-react";
import { PAD2 } from "../data/pad2";
import { ShellV2 } from "./shell-v2";

// ---------------------------------------------------------------------------
// PAD2-spezifische Mock-Erweiterungen, Stand 21.05.2026
// ---------------------------------------------------------------------------

const PAD2_DEVICES = [
  { model: "Motorola R7", icon: Radio, total: 107, in_service: 95, in_stock: 8, in_rma: 3, missing: 1 },
  { model: "Motorola DP4800e", icon: Smartphone, total: 202, in_service: 180, in_stock: 15, in_rma: 7, missing: 0 },
  { model: "Motorola DM4600 (Mobil)", icon: Radio, total: 8, in_service: 8, in_stock: 0, in_rma: 0, missing: 0 },
] as const;

const PAD2_BATTERIES = {
  total: 612,
  avg_health_pct: 87,
  health_min: 62,
  health_max: 99,
  chargers: 24,
  charger_model: "IMPRES NNTN8895 (6-Bay)",
} as const;

const PAD2_RMA = [
  {
    rma_id: "RMA-2026-0428", model: "Motorola DP4800e", serial: "478JKL0042", radio_id: "1042",
    issue: "Display defekt", sent_at: "2026-05-08", eta: "2026-05-28", vendor: "Motorola Solutions DE",
  },
  {
    rma_id: "RMA-2026-0431", model: "Motorola R7", serial: "478MNO1234", radio_id: "1108",
    issue: "Akku lädt nicht mehr", sent_at: "2026-05-12", eta: "2026-06-05", vendor: "Motorola Solutions DE",
  },
  {
    rma_id: "RMA-2026-0435", model: "Motorola DP4800e", serial: "478JKL0078", radio_id: "1078",
    issue: "PTT-Taste klemmt", sent_at: "2026-05-14", eta: "2026-05-30", vendor: "Spreenauten Werkstatt",
  },
  {
    rma_id: "RMA-2026-0440", model: "Motorola R7", serial: "478MNO5689", radio_id: "1145",
    issue: "Wasserschaden, Mikrofon defekt", sent_at: "2026-05-17", eta: "2026-06-12", vendor: "Motorola Solutions DE",
  },
] as const;

const PAD2_CODEPLUGS_BY_MODEL = [
  {
    model: "Motorola SLR5500", subtitle: "Repeater (Master + 3 Peers)",
    versions: [
      { version: "v1.4.2", department: "Site-Standard", deployed_at: "2026-05-14", note: "Capacity Plus Single Site, alle 4 Repeater synchronisiert" },
    ],
  },
  {
    model: "Motorola R7", subtitle: "Handheld · 95 Geräte im Field-Use",
    versions: [
      { version: "v1.4.2", department: "Site-Standard", deployed_at: "2026-05-14", note: "Standard für alle Field-User" },
      { version: "v1.2.0-OPS", department: "Operations", deployed_at: "2026-04-22", note: "Eigene Talkgroups + Notruf-Override" },
      { version: "v1.1.5-IT", department: "IT / Onsite-Tech", deployed_at: "2026-03-10", note: "Eigene Talkgroup + 2 Scan-Listen" },
    ],
  },
  {
    model: "Motorola DP4800e", subtitle: "Handheld · 180 Geräte im Field-Use",
    versions: [
      { version: "v3.2.1", department: "Site-Standard", deployed_at: "2026-05-14", note: "Standard-Codeplug" },
      { version: "v3.1.0-OPS", department: "Operations", deployed_at: "2026-04-22", note: "Loss-Prevention-TG aktiviert" },
    ],
  },
  {
    model: "Motorola DM4600", subtitle: "Mobil-Funkgerät · 8 Geräte (Stapler + Pforte)",
    versions: [
      { version: "v2.0.3", department: "Site-Standard", deployed_at: "2026-04-22", note: "Mobil-Codeplug für Stapler-Fahrer & Pforte" },
    ],
  },
] as const;

const PAD2_SECRETS = [
  {
    label: "AES-256 Verschlüsselungs-Key", sub: "Talkgroup TS1 + TS2",
    kind: "funk", last_rotated: "2026-05-14", rotation_due: "2026-08-12",
  },
  {
    label: "SLR5500 Master Repeater · CPS-Programmier-Passwort", sub: "Motorola CPS",
    kind: "repeater", last_rotated: "2026-03-15", rotation_due: "2026-06-13",
  },
  {
    label: "SLR5500 Web-Admin Login", sub: "Alle 4 Repeater",
    kind: "repeater", last_rotated: "2026-02-01", rotation_due: "2026-05-02",
  },
  {
    label: "SLR5500 Telnet:8501 Reset-Passwort", sub: "Notfall-Reset über Console",
    kind: "repeater", last_rotated: "2025-12-10", rotation_due: "2026-03-10",
  },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
}

function daysUntil(iso: string): number {
  const target = new Date(iso + "T00:00:00").getTime();
  const today = new Date().setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function fmtKb(kb: number): string {
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
}

const SITE_TYPE_LABELS: Record<string, string> = {
  FC: "Fulfillment Center", AMZL: "Last-Mile-Standort", AMXL: "Heavy & Bulky",
  ATS: "Transportstandort", VENDORFLEX: "Vendor Flex", SORTCENTER: "Sort Center", OTHER: "Sonstige",
};

const ACTIVITY_LABELS: Record<string, string> = {
  install: "Installation", maintenance: "Wartung", survey: "Coverage-Survey",
  audit: "Audit", repair: "Reparatur",
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SiteDetailV22() {
  const { code } = useParams<{ code: string }>();
  const isPad2 = (code ?? "PAD2").toUpperCase() === "PAD2";

  if (!isPad2) {
    return (
      <ShellV2 back={{ to: "/preview/landing-v2", label: "Zur Übersicht" }}>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <h1 className="text-2xl font-bold tracking-tight">Detail-Seite „{code}" noch nicht verfügbar</h1>
          <p className="mt-2 text-base">
            Im Design-Preview ist nur <strong>PAD2</strong> vollständig hinterlegt. Springe zu{" "}
            <a className="font-semibold underline underline-offset-4" href="/preview/sites/PAD2/v2-2">PAD2</a>.
          </p>
        </div>
      </ShellV2>
    );
  }

  const site = PAD2;
  const onlineRepeaters = site.repeaters.filter((r) => r.online).length;
  const offlineRepeaters = site.repeaters.length - onlineRepeaters;
  const earliestFreqExpiry = [...site.frequencies].sort((a, b) =>
    a.valid_until.localeCompare(b.valid_until),
  )[0];
  const lastVisit = site.crew_log.at(-1) ?? site.crew_log[0];
  const totalDevices = PAD2_DEVICES.reduce((acc, d) => acc + d.total, 0);
  const inServiceDevices = PAD2_DEVICES.reduce((acc, d) => acc + d.in_service, 0);
  const inRmaDevices = PAD2_DEVICES.reduce((acc, d) => acc + d.in_rma, 0);
  const heatmapDocs = site.documents.filter((d) => d.type === "heatmap_pdf");
  const annualFreqCost = site.frequencies.reduce((acc, f) => acc + f.annual_cost_eur, 0);
  const expiresInDays = daysUntil(earliestFreqExpiry.valid_until);

  return (
    <ShellV2 kicker={site.code} back={{ to: "/preview/landing-v2", label: "Zur Übersicht" }}>
      {/* Hero */}
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">
                <CircleDot className="size-3" aria-hidden /> Aktiv im Betrieb
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {SITE_TYPE_LABELS[site.business_unit] ?? site.business_unit}
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {site.country}
              </span>
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">{site.code}</h1>
            <p className="text-xl text-slate-700 dark:text-slate-200">
              {site.name.replace(`${site.code} — `, "")}
            </p>
            <p className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
              <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{site.address}</span>
            </p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-5 text-sm sm:min-w-[280px] dark:bg-slate-800/60">
            <div className="grid grid-cols-1 gap-3">
              <ContactRow label="Spreenauten POC" value={site.poc_spreenauten} />
              <ContactRow label="Amazon POC" value={site.poc_amazon} />
              <ContactRow label="Letzter Vor-Ort-Besuch" value={`${lastVisit.visitor} · ${formatDate(lastVisit.date)}`} />
            </div>
          </div>
        </div>
      </section>

      {/* At-a-glance — 8 KPIs (2 Reihen × 4) */}
      <section className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <BigFact icon={<Smartphone className="size-5" aria-hidden />} label="Funkgeräte"
          primary={`${totalDevices} Stück`}
          secondary={`${inServiceDevices} aktiv im Einsatz`} />
        <BigFact icon={<Battery className="size-5" aria-hidden />} label="Akku-Gesundheit"
          primary={`Ø ${PAD2_BATTERIES.avg_health_pct} %`}
          secondary={`${PAD2_BATTERIES.total} Akkus · ${PAD2_BATTERIES.health_min}–${PAD2_BATTERIES.health_max} %`}
          tone={PAD2_BATTERIES.avg_health_pct < 75 ? "warn" : "ok"} />
        <BigFact icon={<RadioTower className="size-5" aria-hidden />} label="Repeater"
          primary={`${site.repeaters.length}× SLR5500`}
          secondary={offlineRepeaters > 0 ? `${onlineRepeaters} online · ${offlineRepeaters} offline` : `${onlineRepeaters} online`}
          tone={offlineRepeaters > 0 ? "warn" : "ok"} />
        <BigFact icon={<Antenna className="size-5" aria-hidden />} label="Antennen"
          primary={`${site.antennas.total}`}
          secondary={`${site.antennas.indoor} indoor · ${site.antennas.outdoor} outdoor`} />
        <BigFact icon={<CalendarClock className="size-5" aria-hidden />} label="Frequenz-Lizenz"
          primary={`bis ${formatDate(earliestFreqExpiry.valid_until)}`}
          secondary={expiresInDays > 0 ? `noch ${Math.round(expiresInDays / 30)} Monate` : "abgelaufen!"}
          tone={expiresInDays < 90 ? "warn" : "ok"} />
        <BigFact icon={<BatteryCharging className="size-5" aria-hidden />} label="Ladestationen"
          primary={`${PAD2_BATTERIES.chargers}`}
          secondary={PAD2_BATTERIES.charger_model} />
        <BigFact icon={<Wrench className="size-5" aria-hidden />} label="In Reparatur (RMA)"
          primary={`${PAD2_RMA.length} Geräte`}
          secondary={`davon ${inRmaDevices} aus PAD2-Bestand`}
          tone={PAD2_RMA.length > 0 ? "warn" : "ok"} />
        <BigFact icon={<ShieldCheck className="size-5" aria-hidden />} label="Verschlüsselung"
          primary="AES-256 aktiv"
          secondary={`Key-Rotation: ${formatDate(PAD2_SECRETS[0].rotation_due)}`} />
      </section>

      {/* Snapshot-Download für Amazon (verschlüsselt + Passwort per Mail) */}
      <SnapshotDownloadCard siteCode={site.code} />

      {/* Section cards */}
      <section className="mt-10 grid gap-4 lg:grid-cols-2">
        {/* Funkgeräte */}
        <DetailSection icon={<Smartphone className="size-5" aria-hidden />} title="Funkgeräte"
          subtitle={`${totalDevices} Geräte · ${inServiceDevices} im Einsatz · ${inRmaDevices} in RMA`}>
          <ul className="space-y-3">
            {PAD2_DEVICES.map((d) => (
              <li key={d.model} className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-800/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <d.icon className="size-4 text-slate-500" aria-hidden />
                    <span className="font-semibold">{d.model}</span>
                  </div>
                  <span className="text-2xl font-bold tracking-tight">{d.total}</span>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
                  <DeviceStat label="Im Einsatz" value={d.in_service} tone="ok" />
                  <DeviceStat label="Im Lager" value={d.in_stock} />
                  <DeviceStat label="In RMA" value={d.in_rma} tone={d.in_rma > 0 ? "warn" : undefined} />
                  <DeviceStat label="Verloren" value={d.missing} tone={d.missing > 0 ? "err" : undefined} />
                </div>
              </li>
            ))}
          </ul>
        </DetailSection>

        {/* Akkus & Ladestationen */}
        <DetailSection icon={<Battery className="size-5" aria-hidden />} title="Akkus & Ladestationen"
          subtitle={`${PAD2_BATTERIES.total} Akkus · ${PAD2_BATTERIES.chargers} Ladestationen`}>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-800/30">
              <div className="text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">
                Bestand
              </div>
              <div className="mt-1 text-3xl font-bold tracking-tight">{PAD2_BATTERIES.total}</div>
              <div className="text-sm text-slate-600 dark:text-slate-300">Akkus insgesamt</div>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-800/30">
              <div className="text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">
                Ø Gesundheit
              </div>
              <div className={`mt-1 text-3xl font-bold tracking-tight ${PAD2_BATTERIES.avg_health_pct >= 80 ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`}>
                {PAD2_BATTERIES.avg_health_pct} %
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-300">
                Range: {PAD2_BATTERIES.health_min}–{PAD2_BATTERIES.health_max} %
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-800/30">
            <BatteryCharging className="size-8 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
            <div>
              <div className="font-semibold">{PAD2_BATTERIES.chargers} Ladestationen vor Ort</div>
              <div className="text-sm text-slate-600 dark:text-slate-300">{PAD2_BATTERIES.charger_model}</div>
            </div>
          </div>
        </DetailSection>

        {/* Frequenzen */}
        <DetailSection icon={<CalendarClock className="size-5" aria-hidden />} title="Frequenzzuteilung"
          subtitle={`${site.frequencies.length} Kanäle · Behörde ${earliestFreqExpiry.authority}`}>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {site.frequencies.map((f) => (
              <li key={f.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-mono text-sm font-semibold">
                    {f.rx.toFixed(4)} / {f.tx.toFixed(4)} MHz
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {f.ref} · {f.bandwidth_khz} kHz
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-medium text-slate-700 dark:text-slate-200">
                    bis {formatDate(f.valid_until)}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Kosten: Amazon · {f.annual_cost_eur} €/Jahr
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <FactStrip>
            <Fact label="Behörde" value={earliestFreqExpiry.authority} />
            <Fact label="Kostenträger" value="Amazon" />
            <Fact label="Gesamt p.a." value={`${annualFreqCost} €`} />
          </FactStrip>
        </DetailSection>

        {/* Verschlüsselung & Zugangsdaten — prominent */}
        <DetailSection icon={<Lock className="size-5" aria-hidden />} title="Verschlüsselung & Zugangsdaten"
          subtitle={`${PAD2_SECRETS.length} Secrets · AES-256 + AWS KMS envelope-encrypted`}>
          <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm dark:border-amber-500/30 dark:bg-amber-500/10">
            <ShieldCheck className="mt-0.5 size-5 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden />
            <div>
              <p className="font-semibold text-amber-900 dark:text-amber-200">
                Hoch verschlüsselt gespeichert · Reveal nur für befugte Personen
              </p>
              <p className="mt-1 text-amber-800 dark:text-amber-300">
                Alle Schlüssel und Zugangsdaten liegen <strong>envelope-encrypted</strong> in
                AWS KMS (Customer-Managed-Key, EU-Region). Eine Anzeige im Klartext löst
                immer ein Audit-Event, eine Mattermost-Benachrichtigung und einen
                30-Sekunden-Countdown bis zum automatischen Re-Hide aus. Rotation alle 90 Tage.
              </p>
            </div>
          </div>
          <ul className="space-y-2">
            {PAD2_SECRETS.map((s) => {
              const overdue = daysUntil(s.rotation_due) < 0;
              return (
                <li key={s.label} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/30">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      {s.kind === "funk" ? (
                        <KeyRound className="size-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
                      ) : (
                        <Lock className="size-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
                      )}
                      <div className="min-w-0">
                        <div className="truncate font-semibold">{s.label}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{s.sub}</div>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-mono text-xs text-slate-400 dark:text-slate-500">••••••••••</div>
                      <button
                        type="button"
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:underline dark:text-amber-300"
                      >
                        <Eye className="size-3" aria-hidden /> Reveal anfordern
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                    <span>Zuletzt rotiert: {formatDate(s.last_rotated)}</span>
                    <span className={overdue ? "text-rose-700 dark:text-rose-400 font-medium" : ""}>
                      Nächste Rotation: {formatDate(s.rotation_due)} {overdue && "(überfällig)"}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </DetailSection>

        {/* Codeplugs pro Modell + Department */}
        <DetailSection icon={<ShieldCheck className="size-5" aria-hidden />} title="Codeplugs"
          subtitle={`${PAD2_CODEPLUGS_BY_MODEL.length} Geräte-Modelle · Site-Standard + Department-Varianten`}>
          <ul className="space-y-4">
            {PAD2_CODEPLUGS_BY_MODEL.map((group) => (
              <li key={group.model}>
                <div className="mb-2">
                  <div className="font-semibold">{group.model}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{group.subtitle}</div>
                </div>
                <ul className="space-y-2">
                  {group.versions.map((v) => (
                    <li key={`${group.model}-${v.version}`}
                        className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-semibold">{v.version}</span>
                          <DepartmentBadge name={v.department} />
                        </div>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          ausgerollt am {formatDate(v.deployed_at)}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{v.note}</p>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </DetailSection>

        {/* RMA / Reparaturen */}
        <DetailSection icon={<Wrench className="size-5" aria-hidden />} title="RMA / Reparaturen"
          subtitle={`${PAD2_RMA.length} Geräte aktuell in Reparatur`}>
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/30">
            <div className="flex items-center gap-2 text-sm">
              <PackageOpen className="size-5 text-slate-500 dark:text-slate-400" aria-hidden />
              <span>Defektes Gerät erfassen und RMA anlegen</span>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
            >
              <Wrench className="size-4" aria-hidden /> RMA anmelden
            </button>
          </div>
          <ul className="space-y-2">
            {PAD2_RMA.map((r) => {
              const eta = daysUntil(r.eta);
              return (
                <li key={r.rma_id} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/30">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">{r.rma_id}</span>
                        <span className="font-semibold">{r.model}</span>
                      </div>
                      <div className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">{r.issue}</div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-xs text-slate-500 dark:text-slate-400">
                        <span>SN: {r.serial}</span>
                        <span>Radio-ID: {r.radio_id}</span>
                        <span>Versand: {formatDate(r.sent_at)}</span>
                        <span>Werkstatt: {r.vendor}</span>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xs text-slate-500 dark:text-slate-400">Rückkehr erwartet</div>
                      <div className={`font-semibold ${eta < 7 ? "text-emerald-700 dark:text-emerald-400" : "text-slate-700 dark:text-slate-200"}`}>
                        {formatDate(r.eta)}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {eta > 0 ? `in ${eta} Tagen` : eta === 0 ? "heute" : `${-eta} Tage überfällig`}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </DetailSection>

        {/* Hardware (Repeater) */}
        <DetailSection icon={<RadioTower className="size-5" aria-hidden />} title="Hardware-Inventar (Repeater)"
          subtitle={`${site.repeaters.length} Repeater · ${site.antennas.total} Antennen`}>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {site.repeaters.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{r.id}</span>
                    {r.role === "master" && (
                      <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white dark:bg-white dark:text-slate-900">
                        Master
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {r.model} · Firmware {r.firmware}
                  </div>
                </div>
                {r.online ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="size-4" aria-hidden /> Online
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-700 dark:text-rose-400">
                    <AlertTriangle className="size-4" aria-hidden /> Offline
                  </span>
                )}
              </li>
            ))}
          </ul>
        </DetailSection>

        {/* Coverage-Heatmaps */}
        <DetailSection icon={<MapPin className="size-5" aria-hidden />} title="Coverage-Heatmaps"
          subtitle={`${heatmapDocs.length} Karten · Letzte Survey ${formatDate(site.last_survey_at)}`}>
          <ul className="grid gap-2 sm:grid-cols-2">
            {heatmapDocs.map((d) => (
              <li key={d.name}
                  className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/60 p-3 text-sm dark:border-slate-800 dark:bg-slate-800/30">
                <FileText className="size-4 text-slate-400" aria-hidden />
                <span className="min-w-0 flex-1 truncate" title={d.name}>{d.name}</span>
                <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">{fmtKb(d.size_kb)}</span>
              </li>
            ))}
          </ul>
        </DetailSection>

        {/* Crew-History */}
        <DetailSection icon={<Users className="size-5" aria-hidden />} title="Crew-History"
          subtitle={`${site.crew_log.length} Einsätze dokumentiert`}>
          <ol className="relative ml-3 space-y-4 border-l border-slate-200 pl-5 dark:border-slate-700">
            {[...site.crew_log].reverse().map((v, idx) => (
              <li key={`${v.date}-${v.visitor}-${idx}`} className="relative">
                <span className="absolute -left-[26px] top-1.5 size-3 rounded-full border-2 border-white bg-slate-300 dark:border-slate-900 dark:bg-slate-600" aria-hidden />
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-semibold">{v.visitor}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {formatDate(v.date)} · {ACTIVITY_LABELS[v.activity] ?? v.activity}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">{v.summary}</p>
              </li>
            ))}
          </ol>
        </DetailSection>

        {/* Dokumente */}
        <DetailSection icon={<FileText className="size-5" aria-hidden />} title="Dokumente"
          subtitle={`${site.documents.length} PDFs & Pläne verfügbar`}>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {site.documents.map((d) => (
              <li key={d.name} className="flex items-center justify-between py-2.5">
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="size-4 shrink-0 text-slate-400" aria-hidden />
                  <span className="truncate text-sm" title={d.name}>{d.name}</span>
                </div>
                <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">{fmtKb(d.size_kb)}</span>
              </li>
            ))}
          </ul>
        </DetailSection>
      </section>

      {/* Footer narrative */}
      <section className="mt-10 rounded-2xl bg-slate-100 p-6 text-sm leading-relaxed text-slate-700 sm:p-8 dark:bg-slate-800/40 dark:text-slate-300">
        <div className="flex items-start gap-3">
          <History className="mt-0.5 size-5 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
          <div>
            <p className="font-semibold text-slate-900 dark:text-slate-100">Was diese Seite leistet</p>
            <p className="mt-1">
              Für jede Site ein vollständiges Lagebild: Funkgeräte-Bestand, Akku-Gesundheit,
              Codeplugs pro Gerätemodell und Department, Verschlüsselung & Zugangsdaten unter
              Audit-Schutz, laufende RMAs mit Rückkehr-ETA und ein direkter Weg eine neue
              RMA anzulegen. Stand der Beispieldaten: 21.05.2026 (Vorschau).
            </p>
          </div>
        </div>
      </section>
    </ShellV2>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (self-contained duplicate of v2-helpers, kept locally)
// ---------------------------------------------------------------------------

function ContactRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex flex-col">
      <span className="text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{value}</span>
    </div>
  );
}

const BIG_FACT_TONE_CLASSES = {
  warn: "text-amber-700 dark:text-amber-400",
  ok: "text-emerald-700 dark:text-emerald-400",
  default: "text-slate-900 dark:text-slate-100",
} as const;

function BigFact({ icon, label, primary, secondary, tone }: Readonly<{
  icon: React.ReactNode; label: string; primary: string; secondary?: string; tone?: "ok" | "warn";
}>) {
  const accent = BIG_FACT_TONE_CLASSES[tone ?? "default"];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        <span className="text-slate-400 dark:text-slate-500">{icon}</span>
        <span>{label}</span>
      </div>
      <div className={`mt-2 text-xl font-bold tracking-tight ${accent}`}>{primary}</div>
      {secondary && <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{secondary}</div>}
    </div>
  );
}

function DetailSection({ icon, title, subtitle, children }: Readonly<{
  icon: React.ReactNode; title: string; subtitle?: string; children: React.ReactNode;
}>) {
  const [open, setOpen] = useState(true);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-900">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
              className="flex w-full items-center justify-between gap-3 rounded-2xl p-5 text-left transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
        <div className="flex items-center gap-3">
          <span className="inline-flex size-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">{icon}</span>
          <div>
            <div className="text-base font-semibold tracking-tight">{title}</div>
            {subtitle && <div className="text-sm text-slate-500 dark:text-slate-400">{subtitle}</div>}
          </div>
        </div>
        <ChevronDown aria-hidden className={`size-5 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="border-t border-slate-100 p-5 dark:border-slate-800">{children}</div>}
    </section>
  );
}

function FactStrip({ children }: Readonly<{ children: React.ReactNode }>) {
  return <div className="mt-4 grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/40">{children}</div>;
}

function Fact({ label, value }: Readonly<{ label: string; value: string | number }>) {
  return (
    <div className="text-center">
      <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

const DEVICE_STAT_TONE = {
  ok: "text-emerald-700 dark:text-emerald-400",
  warn: "text-amber-700 dark:text-amber-400",
  err: "text-rose-700 dark:text-rose-400",
  default: "text-slate-900 dark:text-slate-100",
} as const;

function DeviceStat({ label, value, tone }: Readonly<{ label: string; value: number; tone?: "ok" | "warn" | "err" }>) {
  const cls = DEVICE_STAT_TONE[tone ?? "default"];
  return (
    <div className="rounded-lg bg-white p-2 dark:bg-slate-900">
      <div className={`text-lg font-bold ${cls}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  );
}

const DEPT_BADGE_COLORS: Record<string, string> = {
  "Site-Standard": "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  Operations: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  "IT / Onsite-Tech": "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
};

function DepartmentBadge({ name }: Readonly<{ name: string }>) {
  const cls = DEPT_BADGE_COLORS[name] ?? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{name}</span>;
}

// ---------------------------------------------------------------------------
// Snapshot-Download Card — verschlüsselter ZIP, Passwort per Mail an @amazon.com
// ---------------------------------------------------------------------------

const AMAZON_EMAIL_RE = /^[^\s@]+@amazon\.com$/i;

function SnapshotDownloadCard({ siteCode }: Readonly<{ siteCode: string }>) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const isValid = AMAZON_EMAIL_RE.test(email.trim());

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    setSubmitted(true);
    // Mock: in Production triggert das einen Audit-Event, generiert eine
    // AES-256-passwortgeschützte ZIP serverseitig, lädt die Datei, und
    // sendet das Passwort als separate E-Mail an die @amazon.com-Adresse.
  }

  function handleReset() {
    setSubmitted(false);
    setEmail("");
  }

  if (submitted) {
    return (
      <section className="mt-8 overflow-hidden rounded-3xl border border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10">
        <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div className="flex items-start gap-3">
            <CheckCircle className="mt-0.5 size-6 shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden />
            <div>
              <h3 className="text-lg font-bold tracking-tight text-emerald-900 dark:text-emerald-100">
                Snapshot-Anforderung erfasst
              </h3>
              <p className="mt-1 text-sm text-emerald-900/90 dark:text-emerald-200">
                Eine verschlüsselte ZIP-Datei mit dem aktuellen Stand von {siteCode} wird
                gleich heruntergeladen. Das <strong>Entschlüsselungs-Passwort</strong> wurde
                soeben separat per E-Mail an{" "}
                <strong className="font-mono">{email}</strong> gesendet.
              </p>
              <p className="mt-2 text-xs text-emerald-800/80 dark:text-emerald-300/80">
                Eine Audit-Notiz mit Zeitstempel, Anforderer und Site-Code wurde geschrieben.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-800 dark:bg-emerald-500 dark:hover:bg-emerald-400 dark:text-emerald-950"
            >
              <Download className="size-4" aria-hidden />
              {siteCode}-snapshot-2026-05-21.zip
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="text-xs text-emerald-800 underline-offset-2 hover:underline dark:text-emerald-300"
            >
              Neue Anforderung
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-8 overflow-hidden rounded-3xl border border-sky-200 bg-sky-50 dark:border-sky-500/30 dark:bg-sky-500/10">
      <div className="p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-300">
            <FileArchive className="size-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold tracking-tight text-sky-950 dark:text-sky-100">
              Daten-Snapshot für Amazon-Audit herunterladen
            </h3>
            <p className="mt-1 text-sm text-sky-900 dark:text-sky-200">
              Lädt den aktuellen Stand von {siteCode} als strukturierten Snapshot
              (JSON + zugehörige PDFs) als <strong>verschlüsselte ZIP</strong>. Das
              Entschlüsselungs-Passwort wird <strong>separat</strong> per E-Mail an
              die angegebene <strong className="font-mono">@amazon.com</strong>
              -Adresse versandt.
            </p>

            <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3 sm:flex-row">
              <label htmlFor="snapshot-email" className="sr-only">
                Amazon-E-Mail-Adresse
              </label>
              <div className="relative flex-1">
                <Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" aria-hidden />
                <input
                  id="snapshot-email"
                  type="email"
                  inputMode="email"
                  autoComplete="off"
                  required
                  placeholder="vorname.nachname@amazon.com"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  className="w-full rounded-full border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 shadow-sm transition-colors placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-500/30"
                />
              </div>
              <button
                type="submit"
                disabled={!isValid}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-sky-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-sky-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:bg-sky-500 dark:hover:bg-sky-400 dark:text-sky-950 dark:disabled:bg-slate-700 dark:disabled:text-slate-500"
              >
                <Download className="size-4" aria-hidden />
                Snapshot anfordern
              </button>
            </form>
            {email && !isValid && (
              <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">
                Nur Adressen auf <strong className="font-mono">@amazon.com</strong> sind
                berechtigt einen Snapshot zu beziehen.
              </p>
            )}

            <ul className="mt-4 grid grid-cols-1 gap-1 text-xs text-sky-900/80 dark:text-sky-200/80 sm:grid-cols-3">
              <li className="flex items-center gap-1.5">
                <Lock className="size-3.5" aria-hidden /> AES-256 verschlüsselt
              </li>
              <li className="flex items-center gap-1.5">
                <Mail className="size-3.5" aria-hidden /> Passwort separat per Mail
              </li>
              <li className="flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" aria-hidden /> Audit-Trail pro Anforderung
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}